/**
 * The shared board's wire format.
 *
 * Points are normalised to 0–1 of the canvas box rather than pixels: the
 * therapist is usually on a laptop and the patient often on a tablet held
 * sideways, and a circle drawn on one has to stay a circle on the other.
 */
export type BoardPoint = {
    x: number;
    y: number;
};

export type BoardStroke = {
    id: string;
    authorId: string;
    /** CSS colour. Ignored while erasing. */
    color: string;
    /** Fraction of the canvas width, so line weight survives a resize too. */
    width: number;
    erase: boolean;
    points: BoardPoint[];
};

/** LiveKit multiplexes one data channel; the topic keeps board traffic apart. */
export const WHITEBOARD_TOPIC = "alchely-whiteboard";

export type BoardMessage =
    | { kind: "stroke-start"; stroke: BoardStroke }
    | { kind: "stroke-points"; strokeId: string; points: BoardPoint[] }
    | { kind: "clear" }
    | { kind: "sync-request" }
    /**
     * Someone put the board up, so it goes up for everyone.
     *
     * Without this the board only ever appeared for whoever pressed the
     * button. Their strokes did reach the other side — the channel lives
     * above the panel, not inside it — but the other person was still looking
     * at the video tiles, with nothing on screen saying a board now existed.
     * It reads from both ends as "the strokes are not syncing"; what actually
     * failed is that only one of the two was looking at the board.
     *
     * There is no matching close. Putting the board away is a private choice
     * — you want to see the other person's face again — and pulling their
     * board off their screen because you glanced away would be the same bug
     * in reverse.
     */
    | { kind: "board-open" }
    // No chunk index or count: the channel is reliable and ordered, and each
    // chunk is merged by stroke id, so applying them as they arrive converges
    // on the same board whether or not the last one has landed yet.
    //
    // `open` rides along because a snapshot request is also the moment a
    // participant who arrived late has to be told the board is already up:
    // the `board-open` that raised it went out before they were listening.
    | { kind: "sync-chunk"; strokes: BoardStroke[]; open?: boolean };

/**
 * LiveKit rejects data packets above roughly 15 KiB. Staying well under it
 * leaves room for the envelope LiveKit adds and for a stroke that turns out
 * longer than expected.
 */
export const MAX_PACKET_BYTES = 12_000;

/**
 * How long pointer movement is collected before one packet goes out. At 50ms
 * a line still looks live to the other side, while a fast scribble sends
 * twenty packets a second instead of one per pointer event.
 */
export const POINT_BATCH_MS = 50;

export type BoardColor = { key: string; value: string };

/** Named rather than raw hex so each one can be labelled for a screen reader. */
export const BOARD_COLORS: readonly BoardColor[] = [
    { key: "ink", value: "#111827" },
    { key: "red", value: "#dc2626" },
    { key: "blue", value: "#2563eb" },
    { key: "green", value: "#16a34a" },
];

export type BoardWidth = { key: string; value: number };

export const BOARD_WIDTHS: readonly BoardWidth[] = [
    { key: "thin", value: 0.003 },
    { key: "medium", value: 0.006 },
    { key: "thick", value: 0.014 },
];

/**
 * Split a whole board into packets small enough for the data channel.
 *
 * A stroke is never split across packets: the receiver would have to stitch
 * halves back together, and a long scribble is still far below the limit.
 * A single stroke that somehow exceeds it gets its own packet and is sent
 * anyway — dropping it silently would lose part of the board.
 */
export function chunkStrokes(
    strokes: BoardStroke[],
    maxBytes: number = MAX_PACKET_BYTES
): BoardStroke[][] {
    const chunks: BoardStroke[][] = [];
    let current: BoardStroke[] = [];
    let currentBytes = 0;

    for (const stroke of strokes) {
        const size = JSON.stringify(stroke).length;
        if (current.length > 0 && currentBytes + size > maxBytes) {
            chunks.push(current);
            current = [];
            currentBytes = 0;
        }
        current.push(stroke);
        currentBytes += size;
    }

    if (current.length > 0) {
        chunks.push(current);
    }
    return chunks;
}

/**
 * Whether this participant is the one that answers a snapshot request.
 *
 * Everyone still holding the board would otherwise reply at once and the
 * newcomer would draw every stroke twice. Lowest identity wins because both
 * sides compute it from the same list and reach the same answer.
 */
export function shouldAnswerSync(
    localIdentity: string,
    otherIdentities: string[],
    requesterIdentity: string
): boolean {
    if (localIdentity === requesterIdentity) return false;
    return otherIdentities
        .filter(
            (identity) =>
                identity !== requesterIdentity && identity !== localIdentity
        )
        .every((identity) => localIdentity < identity);
}

/**
 * Whether a participant is allowed to wipe the board for everyone.
 *
 * Clearing destroys the other person's work, so it belongs to the therapist.
 * The rule reads the identity minted by the meeting service — `therapist-*`
 * for an account holder, `guest-*` for someone on a share link — and both
 * sides apply it: the sender to decide whether to offer the control, and the
 * receiver to decide whether to obey the packet. Checking only on the sending
 * side would leave the rule enforced by a hidden button, which anyone with a
 * console can work around.
 */
export function mayClearBoard(identity: string): boolean {
    return identity.startsWith("therapist-");
}
